import React from 'react';
import { formatAdminUtcDate } from '../../lib/adminDates';
import { getDocumentStatePresentation } from '../../lib/legalDocumentPresentation';
import type { AdminDocumentPackageListItem } from '../../legalTypes';
import { AdminDataTable, StatusPill, type AdminDataTableColumn } from '../components';

const columns: AdminDataTableColumn<AdminDocumentPackageListItem>[] = [
  {
    id: 'company',
    header: 'Customer',
    accessor: (item) => item.companyLegalName,
    render: (item) => <strong>{item.companyLegalName}</strong>,
    sortable: true,
    width: 'minmax(210px, 1.4fr)',
  },
  {
    id: 'package',
    header: 'Package',
    accessor: (item) => `${item.purpose} ${item.sequence}`,
    render: (item) => (
      <span className="admin-legal-contact">
        <span>Package v{item.sequence}</span>
        <span className="admin-legal-contact__secondary">{item.purpose.replace(/_/g, ' ')}</span>
      </span>
    ),
    sortable: true,
    width: 'minmax(190px, 1fr)',
  },
  {
    id: 'state',
    header: 'Lifecycle state',
    accessor: (item) => item.state,
    render: (item) => {
      const presentation = getDocumentStatePresentation(item.state);
      return (
        <StatusPill tone={presentation.tone === 'danger' ? 'critical' : presentation.tone}>
          <span aria-hidden="true">{presentation.marker}</span> {presentation.label}
        </StatusPill>
      );
    },
    sortable: true,
    width: '180px',
  },
  {
    id: 'updated',
    header: 'Last updated',
    accessor: (item) => item.updatedAtUtc,
    render: (item) => <time data-visual-mask dateTime={item.updatedAtUtc}>{formatAdminUtcDate(item.updatedAtUtc)}</time>,
    sortable: true,
    width: '130px',
  },
];

interface DocumentPackageTableProps {
  packages: AdminDocumentPackageListItem[];
  onOpen: (item: AdminDocumentPackageListItem) => void;
  emptyState: React.ReactNode;
}

const DocumentPackageTable: React.FC<DocumentPackageTableProps> = ({
  packages,
  onOpen,
  emptyState,
}) => (
  <AdminDataTable
    ariaLabel="Document packages"
    rows={packages}
    columns={columns}
    getRowId={(item) => item.id}
    getRowLabel={(item) => `${item.companyLegalName} package version ${item.sequence}`}
    onRowActivate={onOpen}
    emptyState={emptyState}
  />
);

export default DocumentPackageTable;
